import { useEffect } from 'react';
import { Heart, Lock, Zap } from 'lucide-react';
import { LandingHeader } from '@/components/LandingHeader';
import { LandingFooter } from '@/components/LandingFooter';
import { useTranslation } from '@/hooks/useTranslation';
import {
  Reveal,
  SpotlightCard,
  Stagger,
  StaggerItem,
} from '@/components/landing/motion-primitives';
import { CtaSection, PageHero, Section, SectionHeading } from '@/components/landing/page-kit';

export default function FoundersPage() {
  const { language } = useTranslation();
  const fr = language !== 'en';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const founders = [
    {
      name: 'Mehdi',
      role: fr ? 'PDG & Co-fondateur, Data & Produit' : 'CEO & Co-founder, Data & Product',
      bio: fr
        ? "Je suis data analyst au quotidien, habitué aux dashboards, aux chiffres et aux fichiers qui s'empilent. Pendant longtemps, j'ai jonglé entre plusieurs comptes, 2 pays, plusieurs projets, tout en essayant d'aider ma famille et de garder un minimum de stabilité. clevia est née d'un besoin très simple : avoir un endroit clair, calme, où ton argent a enfin du sens, sans devoir être \"bon en finance\" ou passer ta vie sur Excel. Si tu t'es déjà senti perdu devant ton compte bancaire, je te promets : tu n'es pas seul, et clevia est pensé exactement pour ce moment-là."
        : "I'm a data analyst by day, used to dashboards, numbers and files piling up. For a long time, I juggled multiple accounts, 2 countries, several projects, while trying to help my family and keep some stability. clevia was born from a simple need: having one clear, calm place where your money finally makes sense, without having to be \"good at finance\" or spend your life on Excel. If you've ever felt lost in front of your bank account, I promise you: you're not alone, and clevia is designed exactly for that moment.",
    },
    {
      name: 'Marouane',
      role: fr ? 'CTO & Co-fondateur, Tech & Produit' : 'CTO & Co-founder, Tech & Product',
      bio: fr
        ? "Je suis développeur, passionné par les produits simples qui fonctionnent vraiment pour les gens, pas seulement pour les fiches techniques. En parlant avec Mehdi, on a vu la même chose : beaucoup d'apps sont soit trop compliquées, soit trop déconnectées de la vraie vie : études, expatriation, premiers salaires, charges, projets, responsabilités… Avec clevia, mon objectif est de construire une app fiable, fluide, qui respecte ta data et t'accompagne au quotidien, sans te prendre la tête."
        : "I'm a developer, passionate about simple products that actually work for people, not just on spec sheets. Talking with Mehdi, we saw the same thing: many apps are either too complicated or too disconnected from real life: studies, expatriation, first salaries, bills, projects, responsibilities… With clevia, my goal is to build a reliable, smooth app that respects your data and supports you daily, without giving you a headache.",
    },
  ];

  const letter = [
    fr
      ? 'Nous, Mehdi et Marouane, nous nous sommes rencontrés pendant notre master en France.'
      : 'Mehdi and Marouane, we met during our master’s degree in France.',
    fr
      ? "Deux étudiants marocains devenus ingénieurs, avec une vie entre le Maroc et la France et les mêmes galères que beaucoup d'entre vous : plusieurs comptes, des fichiers Excel, des notes un peu partout… mais jamais une vision vraiment claire de notre argent."
      : 'Two Moroccan students who became engineers, with a life between Morocco and France and the same struggles as many of you: multiple accounts, Excel files, notes everywhere... but never a truly clear view of our money.',
    fr
      ? "On s'est surtout rendu compte que très peu d'outils étaient pensés pour notre réalité : celle des Marocains, des Marocaines et, plus largement, de toutes les personnes qui vivent entre deux pays. Des revenus qui évoluent, des comptes dans plusieurs devises, des transferts à la famille, des charges ici et là-bas… sans endroit simple pour tout centraliser."
      : 'We realised that very few tools were designed for our reality: Moroccans and, more broadly, everyone living between two countries. Changing income, accounts in several currencies, transfers to family, expenses here and there... with no simple place to centralise it all.',
  ];

  const values = [
    {
      icon: Zap,
      title: fr ? "La simplicité d'abord" : 'Simplicity first',
      description: fr
        ? 'On conçoit pour de vraies personnes, pas pour des tableurs. Pas de complexité, juste de la clarté.'
        : 'We design for real people, not for spreadsheets. No complexity, just clarity.',
    },
    {
      icon: Heart,
      title: fr ? 'Vos données, votre choix' : 'Your data, your choice',
      description: fr
        ? 'On ne stocke jamais vos identifiants bancaires. Vous restez maître de vos données, toujours.'
        : 'We never store your banking credentials. You stay in control, always.',
    },
    {
      icon: Lock,
      title: fr ? 'Votre vie financière reste la vôtre' : 'Your financial life stays yours',
      description: fr
        ? "Vos transactions et informations personnelles restent privées. Nous n'y accédons que lorsque c'est nécessaire et avec votre accord, par exemple si vous nous demandez de l'aide."
        : 'Your transactions and personal information remain private. We only access them when necessary and with your consent, for example when you ask us for help.',
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <LandingHeader />

      <main>
        <PageHero
          eyebrow={fr ? 'Les fondateurs' : 'The founders'}
          title={fr ? 'Une idée née de' : 'Built by two engineers'}
          highlight={fr ? 'notre quotidien' : 'for everyone'}
          subtitle={
            fr
              ? "Deux ingénieurs marocains, une vie entre deux pays, et l'envie d'un outil qui parle enfin de la vraie vie."
              : 'Two Moroccan engineers, a life between two countries, and the urge for a tool that finally speaks about real life.'
          }
        />

        {/* ── La lettre ──────────────────────────────────────────── */}
        <Section width="narrow" className="pt-0 lg:pt-0">
          <Reveal>
            <div className="rounded-xl border border-border bg-card p-8 sm:p-12">
              <Stagger stagger={0.08}>
                {letter.map((paragraph, i) => (
                  <StaggerItem key={i}>
                    <p
                      className={`text-pretty leading-relaxed text-muted-foreground ${
                        i > 0 ? 'mt-6' : ''
                      }`}
                    >
                      {paragraph}
                    </p>
                  </StaggerItem>
                ))}

                <StaggerItem>
                  <p className="mt-8 text-balance text-xl font-medium tracking-tight text-brand-700 dark:text-brand-400">
                    {fr ? "C'est de là qu'est née clevia." : "That's how clevia was born."}
                  </p>
                </StaggerItem>

                <StaggerItem>
                  <p className="mt-6 text-pretty leading-relaxed text-muted-foreground">
                    {fr
                      ? "Notre objectif est simple : rendre la gestion de l'argent plus claire, plus accessible et plus adaptée à la vraie vie."
                      : 'Our goal is simple: make money management clearer, more accessible and better suited to real life.'}
                  </p>
                </StaggerItem>

                <StaggerItem>
                  <div className="mt-10 border-t border-border pt-8">
                    <p className="text-pretty leading-relaxed text-muted-foreground">
                      {fr
                        ? "Merci d'être là avec nous. Et ce n'est que le début 💚"
                        : 'Thank you for being here with us. And this is only the beginning 💚'}
                    </p>
                    <p className="mt-6 font-medium">
                      Mehdi &amp; Marouane
                      <span className="mt-1 block text-sm font-normal text-muted-foreground">
                        {fr ? 'Fondateurs de clevia' : 'Founders of clevia'}
                      </span>
                    </p>
                  </div>
                </StaggerItem>
              </Stagger>
            </div>
          </Reveal>
        </Section>

        {/* ── Les fondateurs ─────────────────────────────────────── */}
        <Section>
          <SectionHeading
            eyebrow={fr ? 'Qui sommes-nous' : 'Who we are'}
            title={fr ? 'Rencontrez les fondateurs' : 'Meet the founders'}
          />

          <Stagger className="mt-16 grid gap-4 md:grid-cols-2" stagger={0.1}>
            {founders.map((founder) => (
              <StaggerItem key={founder.name}>
                <SpotlightCard className="h-full rounded-xl border border-border bg-card p-8 transition-shadow duration-300 hover:shadow-lg">
                  <h3 className="text-xl font-medium">{founder.name}</h3>
                  <p className="mt-2 text-[11px] font-medium uppercase tracking-[0.18em] text-brand-700 dark:text-brand-400">
                    {founder.role}
                  </p>
                  <p className="mt-6 text-pretty text-sm leading-relaxed text-muted-foreground">
                    {founder.bio}
                  </p>
                </SpotlightCard>
              </StaggerItem>
            ))}
          </Stagger>
        </Section>

        {/* ── Ce qui nous guide ──────────────────────────────────── */}
        <Section tone="dark">
          <div className="grid gap-14 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-20">
            <SectionHeading
              eyebrow={fr ? 'Nos principes' : 'Our principles'}
              title={fr ? 'Ce qui nous guide' : 'What drives us'}
              tone="dark"
            />

            <Stagger className="grid gap-x-10 gap-y-10 sm:grid-cols-2" stagger={0.09}>
              {values.map((value) => (
                <StaggerItem key={value.title}>
                  <div className="border-t border-white/10 pt-6 transition-colors duration-300 hover:border-[#16c47f]/50">
                    <value.icon className="h-5 w-5 text-[#16c47f]" />
                    <h3 className="mt-4 text-balance text-base font-medium text-white">
                      {value.title}
                    </h3>
                    <p className="mt-2 text-pretty text-sm leading-relaxed text-gray-400">
                      {value.description}
                    </p>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </Section>

        {/* ── CTA final ──────────────────────────────────────────── */}
        <CtaSection
          title={
            fr
              ? 'Commencez à construire votre clarté financière'
              : 'Start building your financial clarity'
          }
          subtitle={
            fr
              ? 'Rejoignez les personnes qui prennent le contrôle de leurs finances avec clevia.'
              : 'Join the people who are taking control of their finances with clevia.'
          }
          action={{ label: fr ? 'Commencer gratuitement' : 'Get started free', to: '/register' }}
          secondaryAction={{ label: fr ? 'En savoir plus' : 'Learn more', to: '/' }}
        />
      </main>

      <LandingFooter />
    </div>
  );
}
